import { store } from '../store';
import { settingsUiSlice } from '@shared/stores/SettingsUiReducer';
import { accountsStoreName, navigate } from '@hwm/ui-lib';
import { handleFulfilledAction, handleMultipleLoadingActions, BaseAccountStoreModel } from '@hwm/ui-lib';
import { AccountModel, UserMainAccountModel } from '@models/Account';
import { IDmaResponse } from '@models/IDmaResponse';
import { createEntityAdapter, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { settingsReducer } from '../entity/settings/SettingsReducer';
import { actionInterceptor } from '../middleware/actionsInterceptorMiddleware';
import { fetchHasFlowLinks, getAllDmas } from './thunks';
import { accountsThunks } from './accounts.thunks';
import { userProfileThunks } from './user-profile.thunks';

export interface AccountStoreState extends BaseAccountStoreModel<AccountModel, UserMainAccountModel> {
  dmas: IDmaResponse[];
}

export const accountsAdapter = createEntityAdapter({
  selectId: (acc: AccountModel) => acc.id || acc['accountId'],
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialState: AccountStoreState = {
  error: null,
  hierarchy: [],
  loadingPending: 0,
  selectedAccount: null,
  subaccounts: accountsAdapter.getInitialState(),
  dmas: [],
  userRole: '',
  userMainAccount: null,
  includeArchived: false,
};

const slice = createSlice({
  name: accountsStoreName,
  initialState,
  reducers: {
    setSelectedAccount(state, action: PayloadAction<{ account: AccountModel }>) {
      state.selectedAccount = action.payload.account;
    },
    setHierarchy(state, action: PayloadAction<{ hierarchy: AccountModel[] }>) {
      state.hierarchy = action.payload.hierarchy;
    },
    accountsReceived(state, action: PayloadAction<{ accounts: AccountModel[] }>) {
      accountsAdapter.setAll(state.subaccounts, action.payload.accounts);
    },
    //
    setUserMainAccount: (state, { payload }: { payload: UserMainAccountModel }) => {
      state.userMainAccount = payload;
    },
    setUserRole: (state, { payload }: { payload: string }) => {
      state.userRole = payload;
    },
    setIncludeArchived: (state, { payload }: { payload: boolean }) => {
      state.includeArchived = payload;
    },
  },
  extraReducers: (builder) => {
    handleMultipleLoadingActions(
      [getAllDmas, accountsThunks.fetchSubaccounts, accountsThunks.fetchById, fetchHasFlowLinks],
      builder
    );
    handleFulfilledAction(getAllDmas, builder, (state, response) => {
      state.dmas = response;
    });
    handleFulfilledAction(accountsThunks.fetchSubaccounts, builder, (state, payload) => {
      accountsAdapter.setAll(state.subaccounts, payload);
    });
    handleFulfilledAction(accountsThunks.fetchById, builder, (state, payload) => {
      state.selectedAccount = payload;
      state.hierarchy = [...(payload?.ancestors ?? []), payload];
    });
    handleFulfilledAction(fetchHasFlowLinks, builder, (state, payload) => {
      state.selectedAccount.hasFlowLinks = payload ?? false;
    });
  },
});

export const accountsReducer = {
  slice,
};

//

actionInterceptor.addActionsCallback<typeof userProfileThunks.getUserProfile.fulfilled>({
  actionTypes: [userProfileThunks.getUserProfile.fulfilled.type],
  callback: ({ dispatch, action }) => {
    dispatch(accountsReducer.slice.actions.setUserMainAccount(action?.payload ?? null));

    const accountId = new URLSearchParams(window.location.search).get('accountId');

    if (accountId) {
      dispatch(accountsThunks.fetchById({ accountId }));
    } else {
      dispatch(
        slice.actions.setSelectedAccount({
          account: action.payload,
        })
      );
      dispatch(
        slice.actions.setHierarchy({
          hierarchy: [action.payload],
        })
      );
    }
  },
});

actionInterceptor.addActionsCallback<typeof accountsThunks.fetchById.fulfilled>({
  actionTypes: [accountsThunks.fetchById.fulfilled.type],
  callback: ({ action: { payload }, dispatch }) => {
    const accountId = (payload?.['id'] ?? payload?.['accountId'])?.toString();

    if (!accountId) return;

    const state = store.getState().accounts;

    dispatch(settingsReducer.thunks.getSettings({ accountId }));
    dispatch(settingsUiSlice.actions.setDetails({ id: accountId }));

    if (payload?.isDma) {
      dispatch(fetchHasFlowLinks({ accountId }));
    }

    dispatch(
      getAllDmas({
        accountId,
        includeArchived: state.includeArchived,
      })
    );
    dispatch(
      accountsThunks.fetchSubaccounts({
        accountId,
        includeArchived: state.includeArchived,
      })
    );
  },
});

actionInterceptor.addActionsCallback<typeof accountsReducer.slice.actions.setSelectedAccount>({
  actionTypes: [accountsReducer.slice.actions.setSelectedAccount.type],
  callback: ({ action: { payload }, dispatch }) => {
    const accountId = (payload?.account?.['id'] ?? payload?.account?.['accountId'])?.toString();

    if (!accountId) return;

    const state = store.getState().accounts;

    dispatch(settingsReducer.thunks.getSettings({ accountId }));
    dispatch(settingsUiSlice.actions.setDetails({ id: accountId }));

    if (payload.account?.isDma) {
      dispatch(fetchHasFlowLinks({ accountId }));
    }

    dispatch(
      getAllDmas({
        accountId,
        includeArchived: state.includeArchived,
      })
    );
    dispatch(
      accountsThunks.fetchSubaccounts({
        accountId,
        includeArchived: state.includeArchived,
      })
    );
  },
});

actionInterceptor.addActionsCallback<typeof accountsThunks.fetchById.rejected, AccountStoreState>({
  actionTypes: [accountsThunks.fetchById.rejected.type],
  callback: ({ dispatch }) => {
    dispatch(userProfileThunks.getUserProfile())
      .then(unwrapResult)
      .then(() => {
        navigate({
          search: {
            accountId: null,
          },
        });
      });
  },
});
